import { IonIcon, IonInput, IonItem } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { lockClosedOutline, mailOutline } from 'ionicons/icons';

import { useAuthActions } from '../../../hooks/Auth/useAuth';
import { useAlert } from '../../../hooks/UI/useAlert';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { Login as LoginType, loginDefaults } from '../../../types/Auth/types';
import { validations } from '../../../utils/validations';
import Alert from '../../UI/Alert';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import Spinner from '../../UI/Spinner';
import './Login.scss';
import { LoginProps } from './types';

const Login: React.FC<LoginProps> = ({ setAuthMode, onLoginSuccess }) => {
  const { login } = useAuthActions();
  const { ref, handleEnter } = useEnter();
  const { alertState, showAlert, hideAlert } = useAlert();
  const isMountedRef = useRef(true);

  const { handleSubmit, control, errors } = useForm<LoginType>({
    defaultValues: loginDefaults,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onSubmit = async (values: LoginType) => {
    setIsLoading(true);

    try {
      const { success, error } = await login(values);

      if (success) {
        onLoginSuccess?.();
        return;
      }

      if (!isMountedRef.current) return;

      showAlert({
        header: 'Error',
        message: error ?? 'Email o contraseña incorrecto/a',
        buttons: [{ text: 'Reintentar' }],
      });
    } catch (error) {
      console.error('Error en login:', error);

      if (!isMountedRef.current) return;

      showAlert({
        header: 'Error',
        message: 'Ocurrió un error inesperado',
        buttons: [{ text: 'Entendido' }],
      });
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  return (
    <form className="login" onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
      <FormItem
        errors={errors}
        control={control}
        name="email"
        rules={{
          required: 'Por favor, ingresa tu email',
          pattern: validations.email,
        }}
        render={({ onChange }) => (
          <IonItem>
            <IonInput
              type="email"
              placeholder="Email"
              onIonChange={(e) => onChange(e.detail.value!)}
            >
              <IonIcon icon={mailOutline} />
            </IonInput>
          </IonItem>
        )}
      />

      <FormItem
        errors={errors}
        control={control}
        name="password"
        rules={{
          required: 'Por favor, ingresa tu contraseña',
        }}
        render={({ onChange }) => (
          <IonItem>
            <IonInput
              type="password"
              placeholder="Contraseña"
              onIonChange={(e) => onChange(e.detail.value!)}
            >
              <IonIcon icon={lockClosedOutline} />
            </IonInput>
          </IonItem>
        )}
      />

      <div className="login__forgetPassword" onClick={() => setAuthMode('password')}>
        ¿Olvidaste tu contraseña?
      </div>

      <div className="ion-margin-top mt-aux">
        <Button ref={ref} type="submit" disabled={isLoading}>
          {isLoading ? <Spinner color="light" /> : 'Iniciar sesión'}
        </Button>
      </div>

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />
    </form>
  );
};

export default Login;
